import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { Pressable, StyleSheet, View } from 'react-native';

import {
  BodyText,
  CloudinaryImage,
  Container,
  Icon,
  LoadingPlaceholder,
  theme,
  useResponsive,
} from '@sg/garnish';

export const HomeRateOrderCard = (props: HomeRateOrderCardProps) => {
  const { isLoading, image, orderCount, onPress } = props;
  const { match, minWidth } = useResponsive();

  const { formatMessage } = useIntl();

  // ───────────────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <Container style={match([styles.containerXS, styles.containerSM])}>
        <LoadingPlaceholder palette="cream" rowHeight={90} />
      </Container>
    );
  }

  return (
    <Container style={match([styles.containerXS, styles.containerSM])}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={formatMessage(messages.ctaA11y)}
        style={styles.card}
        onPress={onPress}
      >
        <View style={styles.content}>
          {image ? (
            <View>
              <CloudinaryImage
                baseUrl={image}
                alt={formatMessage(messages.imageAlt)}
                config={IMAGE_CONFIG}
                style={styles.image}
              />

              {orderCount > 1 && (
                <BodyText sizeMatch={['12']} style={styles.orderCount}>
                  + {orderCount}
                </BodyText>
              )}
            </View>
          ) : null}

          <View>
            <BodyText sizeMatch={['16']} bold>
              <FormattedMessage {...messages.title} />
            </BodyText>

            <BodyText style={styles.description} sizeMatch={['14']}>
              <FormattedMessage {...messages.description} />
            </BodyText>
          </View>
        </View>

        {minWidth.isSM ? (
          <View style={styles.cta}>
            <BodyText sizeMatch={['16']}>
              <FormattedMessage {...messages.cta} />
            </BodyText>

            <Icon width={16} height={16} name="IconStarStroke" />
          </View>
        ) : null}
      </Pressable>
    </Container>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const IMAGE_SIZE = 56;
const IMAGE_CONFIG = [{ crop: 'crop' as const, width: 0.95, height: 0.95 }, { width: IMAGE_SIZE }];

const styles = StyleSheet.create({
  containerXS: {
    paddingHorizontal: theme.spacing['4'],
  },
  containerSM: {
    width: '100%',
  },
  card: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing['4'],
    gap: theme.spacing['4'],
    borderRadius: theme.radius.large,
    borderWidth: 1,
    borderColor: theme.colors.LIGHT,
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['4'],
  },
  cta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['2'],
    paddingHorizontal: theme.spacing['4'],
  },
  description: {
    color: theme.colors.CHARCOAL,
  },
  image: {
    width: IMAGE_SIZE,
    height: IMAGE_SIZE,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
    borderRadius: theme.radius.medium,
  },
  orderCount: {
    position: 'absolute',
    bottom: theme.spacing['1'],
    right: theme.spacing['1'],
    fontSize: 10,
    lineHeight: 16,
    letterSpacing: -1,
    paddingVertical: 1,
    paddingHorizontal: theme.spacing['1'],
    borderRadius: theme.radius.small,
    color: theme.colors.WHITE,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.DARKER,
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'How was it?',
    description: 'Home | Rate Order Card | Title',
  },
  description: {
    defaultMessage: 'Rate your last order.',
    description: 'Home | Rate Order Card | Description',
  },
  cta: {
    defaultMessage: 'Rate',
    description: 'Home | Rate Order Card | CTA',
  },
  imageAlt: {
    defaultMessage: 'Product image for this order',
    description: 'Home | Rate Order Card | Image Alt',
  },
  ctaA11y: {
    defaultMessage: 'Rate this order',
    description: 'Home | Rate Order Card | CTA A11y',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type HomeRateOrderCardProps = {
  isLoading: boolean;
  image: string;
  orderCount: number;
  onPress: () => void;
};
